import { createContext, useContext } from "react";
import { GaiaDrawModeType, EnabledToolTypes, MapWmsLayer } from "../GaiaMap";
import { LatLng, FeatureGroup, Layer, Polyline } from "leaflet";
import { GetBoundaryRequest, GetBoundaryResponse } from "@shared/mirage/core/contracts/GetBoundaryRequest";
import { ClonePreviousGeometryRequest, ClonePreviousGeometryResponse } from "@shared/planningmanagement/contracts/IClonePreviousGeometry";
import { LineString, MultiLineString } from "@turf/helpers";
import { GetFeatureInfoRequest } from "@shared/mirage/services/WmsCapabilities";
import { GetFeatureInfoResponse } from "@shared/mirage/services/WmsCapabilities";
import { EarthlightMapBoxSize } from "@shared/mirage/services/IEarthlightService";

export interface MapHistoryState {
    points: LatLng[];
    featureGroup: FeatureGroup;
    isDrawing: boolean;
    drawMode: GaiaDrawModeType;
    isComplete?: boolean;
}

export interface GaiaMapContext {
    onMapBoxSizeRequested?: () => Promise<EarthlightMapBoxSize | null>;
    mapBoxSize: EarthlightMapBoxSize | null;
    setDrawMode: (mode: GaiaDrawModeType) => void;
    drawMode: GaiaDrawModeType;
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
    pushHistory: (state: MapHistoryState, isDrawing?: boolean, isImport?: boolean) => void;
    getUndoState: () => MapHistoryState | null;
    getRedoState: () => MapHistoryState | null;
    acceptDrawing: () => void;
    cancelDrawing: () => void;
    hasGeometry: boolean;
    hasLabels: boolean;
    acceptLabels: () => void;
    cancelLabels: () => void;
    readOnly: boolean;
    singleGeometryMode: boolean;
    onClone?: (req: GetBoundaryRequest) => Promise<GetBoundaryResponse>;
    center: L.LatLngExpression | null;
    centerMap: (center: L.LatLngExpression | null) => void;
    setGeometryFromGeoJSON: (geoJsonString: string | null | undefined, options?: { fitBounds?: boolean }) => { success: boolean; didFitBounds?: boolean };
    layerOnClick: (e: L.LeafletMouseEvent, layer: L.Polygon<any> | Polyline<LineString | MultiLineString, any> | Layer) => void;
    workingGeometry: string | null;
    updateWorkingGeometry: (geometry: string | null) => void;
    featureGroupToWorkingGeometry: (featureGroup: React.MutableRefObject<L.FeatureGroup>) => string | null;
    enabledToolTypes?: EnabledToolTypes[];
    onChange?: (geometry: string | null, lastDrawnGeometry: string | null) => void;
    disableUndoRedo?: boolean;
    disableAcceptDrawing?: boolean;
    onSelect?: (drawnPolygon: GeoJSON.Feature<GeoJSON.Polygon>, actionType: "select" | "deselect") => void;
    getFeatureInfo?: (req: GetFeatureInfoRequest) => Promise<GetFeatureInfoResponse>;
    wmsLayers: MapWmsLayer[];
    wmsUrl?: string | null;
    activatePrintPreview?: () => void;
    printLoading: boolean;
    startPrintLoading: () => void;
    stopPrintLoading: () => void;
    pointCustomIcon?: L.Icon;
    isFullScreen: boolean;
    setIsFullScreen: React.Dispatch<React.SetStateAction<boolean>>;
    pointAsCircleMarker: boolean;
    suppressNextFit: () => void;
    onClonePreviousGeometry?: (req: ClonePreviousGeometryRequest) => Promise<ClonePreviousGeometryResponse>;
}

const GaiaMapContext = createContext<GaiaMapContext>({
    setDrawMode: (_: GaiaDrawModeType) => undefined,
    drawMode: null,
    canUndo: false,
    canRedo: false,
    onUndo: () => undefined,
    onRedo: () => undefined,
    pushHistory: () => undefined,
    getUndoState: () => null,
    getRedoState: () => null,
    acceptDrawing: () => undefined,
    cancelDrawing: () => undefined,
    hasGeometry: false,
    hasLabels: false,
    acceptLabels: () => undefined,
    cancelLabels: () => undefined,
    readOnly: false,
    singleGeometryMode: false,
    onClone: undefined,
    center: null,
    centerMap: () => undefined,
    setGeometryFromGeoJSON: () => ({ success: false }),
    layerOnClick: () => undefined,
    workingGeometry: null,
    updateWorkingGeometry: () => undefined,
    featureGroupToWorkingGeometry: () => null,
    enabledToolTypes: undefined,
    onChange: undefined,
    disableUndoRedo: false,
    disableAcceptDrawing: false,
    onSelect: undefined,
    getFeatureInfo: undefined,
    wmsLayers: [],
    wmsUrl: undefined,
    activatePrintPreview: undefined,
    printLoading: false,
    startPrintLoading: () => undefined,
    stopPrintLoading: () => undefined,
    mapBoxSize: null,
    onMapBoxSizeRequested: undefined,
    pointCustomIcon: undefined,
    isFullScreen: false,
    setIsFullScreen: () => undefined,
    pointAsCircleMarker: false,
    suppressNextFit: () => undefined,
    onClonePreviousGeometry: undefined,
});

export const useGaiaMapContext = () => useContext(GaiaMapContext);

export const GaiaMapProvider = GaiaMapContext.Provider;
